// The platform ban and its lifting (spec/07 §3.5, F-IAM-07): `POST /v1/hq/players/{p}/ban|unban`,
// `hq:players` — `hq_owner`'s alone. Shown only to a role that holds it (`PlayerPage.tsx`); the
// server refuses anyone else, and refuses a ban of one's own account.
//
// Both need a reason, and both say what they will do before they do it (spec/13 §2): a ban stops the
// person signing in anywhere and signs them out everywhere at once; lifting it lets them sign in
// again. Sent to someone already banned, a ban changes that ban's reason and length. A ban lasts
// until it is lifted, or for a length from now — a length rather than a date, so no time zone has to
// be guessed at.

import type { BanPlayerOnPlatformBody, HqPlayer, HqPlayerList, PlatformBan } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Button, Dialog, DialogClose, RadioGroup } from '@clavian/ui-web';
import { useMutation, useQueryClient, type InfiniteData, type QueryClient } from '@tanstack/react-query';
import { useId, useState, type FormEvent } from 'react';

import { useApi } from '../../api/client';
import { fieldErrors, problemCode, PROBLEM } from '../../api/problems';
import { queryKeys } from '../../api/queries';
import { LoadFailure } from '../../shell/LoadFailure';
import { ReasonField } from '../ReasonField';
import { BAN_REASON_MAX } from './names';

const DAY_MS = 86_400_000;

/** How long a ban lasts: until it is lifted, a number of days from now, or the end it already has. */
const LENGTHS = ['lifted', '1', '7', '30', '90'] as const;
type Length = (typeof LENGTHS)[number] | 'keep';

export interface BanActionsProps {
  readonly player: HqPlayer;
  /** What the page calls them. */
  readonly name: string;
  /** Said on the page once a ban or its lifting is saved. */
  readonly onSaved: (message: string) => void;
}

export function BanActions({ player, name, onSaved }: BanActionsProps) {
  return (
    <div className="console-hq-actions">
      {player.platformBan.banned ? (
        <>
          <BanDialog player={player} name={name} change onSaved={onSaved} />
          <LiftDialog player={player} name={name} onSaved={onSaved} />
        </>
      ) : (
        <BanDialog player={player} name={name} change={false} onSaved={onSaved} />
      )}
    </div>
  );
}

/** The record and every search result that shows them, as the ban now stands — without a new read. */
function applyBan(queryClient: QueryClient, playerId: string, ban: PlatformBan): void {
  queryClient.setQueryData<HqPlayer>(queryKeys.hqPlayer(playerId), (old) =>
    old === undefined ? old : { ...old, banned: ban.banned, platformBan: ban },
  );
  queryClient.setQueriesData<InfiniteData<HqPlayerList>>({ queryKey: queryKeys.hqPlayerSearches }, (old) =>
    old === undefined
      ? old
      : {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            data: page.data.map((row) => (row.id === playerId ? { ...row, banned: ban.banned } : row)),
          })),
        },
  );
}

function BanDialog({ player, name, change, onSaved }: BanActionsProps & { readonly change: boolean }) {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  const api = useApi();
  const queryClient = useQueryClient();
  const formId = useId();
  const current = player.platformBan;
  const [open, setOpen] = useState(false);
  // "Keep its end" only for a ban that has one still ahead of it, as of opening the dialog.
  const [canKeep, setCanKeep] = useState(false);
  const [reason, setReason] = useState('');
  const [length, setLength] = useState<Length>('lifted');
  const [missing, setMissing] = useState(false);

  const mutation = useMutation({
    mutationFn: (body: BanPlayerOnPlatformBody) => api.banPlayerOnPlatform({ path: { playerId: player.id }, body }),
    onSuccess: (ban) => {
      applyBan(queryClient, player.id, ban);
      setOpen(false);
      onSaved(change ? t('players.ban.saved.changed') : t('players.ban.saved.banned'));
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const why = reason.trim();
    setMissing(why === '');
    if (why === '') return;
    const expiresAt =
      length === 'lifted' ? null : length === 'keep' ? current.expiresAt : new Date(Date.now() + Number(length) * DAY_MS).toISOString();
    mutation.mutate({ reason: why, expiresAt });
  };

  const errors = fieldErrors(mutation.error);
  const reasonError = missing ? t('players.ban.reasonRequired') : errors.find((error) => error.field === 'reason')?.message;
  const lengthError = errors.find((error) => error.field === 'expiresAt')?.message;
  const options = [
    ...(canKeep && current.expiresAt !== null
      ? [{ value: 'keep', label: t('players.ban.dialog.keep', { when: format.dateTime(current.expiresAt, { date: 'medium', time: 'short' }) }) }]
      : []),
    ...LENGTHS.map((value) => ({ value, label: t(`players.ban.dialog.lengths.${value}`) })),
  ];

  return (
    <Dialog
      title={change ? t('players.ban.dialog.changeTitle', { name }) : t('players.ban.dialog.banTitle', { name })}
      description={change ? t('players.ban.dialog.changeConsequence') : t('players.ban.dialog.banConsequence')}
      trigger={<Button variant={change ? 'secondary' : 'danger'}>{change ? t('players.ban.action.change') : t('players.ban.action.ban')}</Button>}
      open={open}
      onOpenChange={(next) => {
        if (next) {
          const keep = change && current.expiresAt !== null && Date.parse(current.expiresAt) > Date.now();
          setCanKeep(keep);
          setReason(change ? (current.reason ?? '') : '');
          setLength(keep ? 'keep' : 'lifted');
          setMissing(false);
          mutation.reset();
        }
        setOpen(next);
      }}
      footer={
        <>
          <DialogClose asChild>
            <Button>{t('players.ban.dialog.cancel')}</Button>
          </DialogClose>
          <Button type="submit" form={formId} variant={change ? 'primary' : 'danger'} busy={mutation.isPending}>
            {change ? t('players.ban.dialog.submitChange') : t('players.ban.dialog.submitBan')}
          </Button>
        </>
      }
    >
      <form id={formId} className="console-hq-dialog-form" noValidate onSubmit={submit}>
        <ReasonField
          label={t('players.ban.dialog.reason')}
          description={t('players.ban.dialog.reasonDescription')}
          value={reason}
          maxLength={BAN_REASON_MAX}
          onChange={setReason}
          error={reasonError}
        />
        <RadioGroup
          label={t('players.ban.dialog.length')}
          options={options}
          value={length}
          onValueChange={(value) => setLength(value as Length)}
          error={lengthError}
        />
        <Failure error={mutation.error} fieldsShown={errors.length > 0} />
      </form>
    </Dialog>
  );
}

function LiftDialog({ player, name, onSaved }: BanActionsProps) {
  const { t } = useTranslation('hq');
  const api = useApi();
  const queryClient = useQueryClient();
  const formId = useId();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [missing, setMissing] = useState(false);

  const mutation = useMutation({
    mutationFn: (why: string) => api.unbanPlayerOnPlatform({ path: { playerId: player.id }, body: { reason: why } }),
    onSuccess: (ban) => {
      applyBan(queryClient, player.id, ban);
      setOpen(false);
      onSaved(t('players.ban.saved.lifted'));
    },
  });

  const errors = fieldErrors(mutation.error);
  const reasonError = missing ? t('players.ban.reasonRequired') : errors.find((error) => error.field === 'reason')?.message;

  return (
    <Dialog
      title={t('players.ban.dialog.liftTitle', { name })}
      description={t('players.ban.dialog.liftConsequence')}
      trigger={<Button>{t('players.ban.action.lift')}</Button>}
      open={open}
      onOpenChange={(next) => {
        if (next) {
          setReason('');
          setMissing(false);
          mutation.reset();
        }
        setOpen(next);
      }}
      footer={
        <>
          <DialogClose asChild>
            <Button>{t('players.ban.dialog.cancel')}</Button>
          </DialogClose>
          <Button type="submit" form={formId} variant="primary" busy={mutation.isPending}>
            {t('players.ban.dialog.submitLift')}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className="console-hq-dialog-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          const why = reason.trim();
          setMissing(why === '');
          if (why !== '') mutation.mutate(why);
        }}
      >
        <ReasonField
          label={t('players.ban.dialog.reason')}
          description={t('players.ban.dialog.liftReasonDescription')}
          value={reason}
          maxLength={BAN_REASON_MAX}
          onChange={setReason}
          error={reasonError}
        />
        <Failure error={mutation.error} fieldsShown={errors.length > 0} />
      </form>
    </Dialog>
  );
}

/** A refusal the fields do not already show, in HQ's words, with the reference to quote. */
function Failure({ error, fieldsShown }: { readonly error: unknown; readonly fieldsShown: boolean }) {
  const { t } = useTranslation('hq');
  if (error === null || error === undefined || fieldsShown) return null;
  const message =
    problemCode(error) === PROBLEM.forbidden
      ? t('players.ban.forbidden')
      : problemCode(error) === PROBLEM.notFound
        ? t('players.ban.nothingThere')
        : t('players.ban.failed');
  return <LoadFailure error={error} message={message} />;
}
